import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, mergeMap, of, switchMap } from 'rxjs';

import { getApiErrorMessage } from '../../../core/utils/api-error.util';
import { PartnerManagementService } from '../../partner-management/services/partner-management.service';
import { DocumentVerificationActions } from './document-verification.actions';

@Injectable()
export class DocumentVerificationEffects {
  private readonly actions$ = inject(Actions);
  private readonly partnerService = inject(PartnerManagementService);

  readonly loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentVerificationActions.loadList),
      switchMap(({ query }) =>
        forkJoin({
          list: this.partnerService.listPartnerDocuments(query),
          stats: this.partnerService.getPartnerDocumentStats(),
        }).pipe(
          map(({ list, stats }) =>
            DocumentVerificationActions.loadListSuccess({
              rows: list.items,
              total: list.total,
              page: list.page,
              pageSize: list.limit,
              stats,
            }),
          ),
          catchError((error: unknown) =>
            of(
              DocumentVerificationActions.loadListFailure({
                error: getApiErrorMessage(error, 'Unable to load document records'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly reviewDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentVerificationActions.reviewDocument),
      switchMap(({ partnerId, documentId, payload, query }) =>
        this.partnerService.reviewPartnerDocument(partnerId, documentId, payload).pipe(
          mergeMap((response) => [
            DocumentVerificationActions.reviewDocumentSuccess({
              message: response.message,
            }),
            DocumentVerificationActions.loadList({ query }),
          ]),
          catchError((error: unknown) =>
            of(
              DocumentVerificationActions.reviewDocumentFailure({
                error: getApiErrorMessage(error, 'Unable to review document'),
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
